import { useCallback, useState } from 'react';

import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import {
  AtomMap,
  latLngToUrlValue,
  MarkerLayer,
  SearchPin,
  urlValueToLatLng,
  useDefaultMapLocation,
} from '@atom/map';
import { MapIdleEvent, MapSearch, ViewState } from '@atom/types/map';

import { useTeamMapData } from './useTeamMapData';

const SEARCH_ZOOM = 15;

const TeamMapView = () => {
  const defaultLocation = useDefaultMapLocation();

  const [
    { center, zoom, searchPoint, searchTerm },
    { navigate, updateSearchParams },
  ] = useNavigation({
    center: SearchParamType.STRING,
    zoom: SearchParamType.NUMBER,
    searchPoint: SearchParamType.STRING,
    searchTerm: SearchParamType.STRING,
  });

  const urlCenter = center ? urlValueToLatLng(center) : null;

  const initialViewState: ViewState = {
    center: urlCenter ?? defaultLocation.center,
    zoom: zoom || defaultLocation.zoom,
    searchPoint: searchPoint ? urlValueToLatLng(searchPoint) : undefined,
    searchTerm: searchTerm ?? undefined,
  };

  const [viewport, setViewport] = useState<MapIdleEvent | null>(null);
  const [mapData, loading] = useTeamMapData({
    coordinates: viewport?.coordinates ?? null,
    zoomLevel: viewport?.zoom ?? null,
  });

  const handleIdle = (view: MapIdleEvent) => {
    setViewport(view);
    updateSearchParams({
      center: latLngToUrlValue(view.center),
      zoom: view.zoom,
    });
  };

  const handleSearchChange = (search: MapSearch) =>
    search.searchPoint
      ? updateSearchParams({
          searchPoint: latLngToUrlValue(search.searchPoint),
          searchTerm: search.searchTerm ?? null,
          center: latLngToUrlValue(search.searchPoint),
          zoom: SEARCH_ZOOM,
        })
      : updateSearchParams({ searchPoint: null, searchTerm: null });

  const handleMarkerClick = useCallback(
    (markerId: string) => {
      navigate(`/team/${markerId}`);
    },
    [navigate],
  );

  const searchPinPosition = searchPoint ? urlValueToLatLng(searchPoint) : null;

  return (
    <AtomMap
      initialViewState={initialViewState}
      onIdle={handleIdle}
      onSearchChange={handleSearchChange}
      loading={loading}
    >
      <MarkerLayer data={mapData} onMarkerClick={handleMarkerClick} />
      <SearchPin position={searchPinPosition} />
    </AtomMap>
  );
};

export default TeamMapView;
